import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ConsentAPI from "../services/ConsentAPI";
import "./css/Consent.css";

/**
 * Component for handling email verification
 * This component is used when users click on the verification link in their email
 */
function VerifyEmail() {
  const { verificationKey } = useParams();
  const [status, setStatus] = useState({
    isLoading: true,
    isVerified: false,
    message: "Verifying your email address...",
    error: "",
    canResend: false,
    userUuid: null,
    email: null,
  });
  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const verifyEmail = async () => {
      // If no key was provided in the URL, show an error
      if (!verificationKey) {
        setStatus({
          isLoading: false,
          isVerified: false,
          message: "",
          error:
            "No verification key was provided. Please check your email and try again.",
        });
        return;
      }

      console.log(
        `[VerifyEmail] Attempting to verify key: ${verificationKey.substring(
          0,
          10,
        )}...`,
      );
      try {
        // Call the API to verify the email key
        const response = await ConsentAPI.verifyConsentKey(verificationKey);

        console.log("Email verification response received:", response);

        // Check if verification was successful
        // Backend returns a Map with 'verified' and 'message' keys
        if (response && response.verified === true) {
          console.log("Email verification successful:", response);

          // Set user as active and verified in local storage if applicable
          if (response.userUuid) {
            try {
              localStorage.setItem("userVerified", "true");
              localStorage.setItem("verifiedUserUuid", response.userUuid);

              // Store password setup token if user needs to set password
              if (response.needsPasswordSetup && response.passwordSetupToken) {
                localStorage.setItem(
                  "passwordSetupToken",
                  response.passwordSetupToken,
                );
              }
            } catch (storageError) {
              console.warn(
                "Could not store verification status in local storage:",
                storageError,
              );
            }
          }

          setStatus({
            isLoading: false,
            isVerified: true,
            message:
              response.message ||
              "Your email address has been verified successfully. Thank you!",
            error: "",
          });

          // Redirect based on whether password setup is needed
          if (response.needsPasswordSetup) {
            // Redirect to password setup page
            setTimeout(() => {
              navigate("/set-password");
            }, 2000);
          } else {
            // Redirect to login page after verification
            setTimeout(() => {
              navigate("/app");
            }, 3000);
          }
        } else {
          // Verification failed - handle as error
          console.log("Email verification failed:", response);

          const errorMessage =
            response && response.message
              ? response.message
              : "Verification failed. The key may be invalid, expired, or already used.";

          setStatus({
            isLoading: false,
            isVerified: false,
            message: "",
            error: errorMessage,
            canResend: response && response.canResend === true,
            userUuid: response && response.userUuid,
            email: response && response.email,
          });
        }
      } catch (error) {
        console.error("Error verifying email:", error);

        let errorMessage =
          "Failed to verify your email address. The verification key may be invalid or expired.";

        // Check if the error has a response with data
        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        setStatus({
          isLoading: false,
          isVerified: false,
          message: "",
          error: errorMessage,
          canResend: false,
          userUuid: null,
          email: null,
        });
      }
    };

    verifyEmail();
  }, [verificationKey, navigate]);

  // Handle resend verification email
  const handleResendVerification = async () => {
    if (!status.userUuid || !status.email) {
      console.error("Cannot resend: missing user information");
      return;
    }

    setIsResending(true);
    try {
      const response = await ConsentAPI.resendVerificationEmail(
        status.userUuid,
        status.email,
      );

      if (response && response.success) {
        setStatus({
          ...status,
          error: "",
          message: `A new verification email has been sent to ${status.email}. Please check your inbox and spam folder.`,
        });
      } else {
        setStatus({
          ...status,
          error:
            response.message ||
            "Failed to resend verification email. Please try again later.",
        });
      }
    } catch (error) {
      console.error("Error resending verification email:", error);
      const errorMsg =
        error.response?.data?.message ||
        "Failed to resend verification email. Please try again later.";
      setStatus({
        ...status,
        error: errorMsg,
      });
    } finally {
      setIsResending(false);
    }
  };

  // Handle retry button click
  const handleRetry = () => {
    setStatus({
      isLoading: true,
      isVerified: false,
      message: "Verifying your email address...",
      error: "",
    });

    // Retry the verification after a short delay
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  // Handle go home button click
  const handleGoHome = () => {
    navigate("/");
  };

  // Handle login click
  const handleLogin = () => {
    // Navigate to login page
    navigate("/login");
  };

  return (
    <div className="verify-consent-container">
      <div className="verify-consent-card">
        <h1>Email Verification</h1>
        {status.isLoading && (
          <div className="verify-message loading-message">
            <p>{status.message}</p>
          </div>
        )}
        {!status.isLoading && status.isVerified && (
          <div className="verify-message success-message">
            <div className="checkmark-icon">✓</div>
            <p>{status.message}</p>
            <p>You will be redirected to the home page shortly...</p>
            <div style={{ marginTop: "20px" }}>
              <button
                className="action-button"
                onClick={handleGoHome}
                style={{ backgroundColor: "#4caf50" }}
              >
                Continue to KinShield
              </button>
            </div>
          </div>
        )}
        {!status.isLoading && !status.isVerified && (
          <div className="verify-message error-message">
            <div className="error-icon">✗</div>
            <p>{status.error}</p>
            {status.message && (
              <p style={{ color: "#4caf50", marginTop: "10px" }}>
                {status.message}
              </p>
            )}
            <div style={{ marginTop: "20px" }}>
              {status.canResend && (
                <button
                  className="action-button"
                  onClick={handleResendVerification}
                  disabled={isResending}
                  style={{
                    backgroundColor: isResending ? "#ccc" : "#ff9800",
                    cursor: isResending ? "not-allowed" : "pointer",
                  }}
                >
                  {isResending ? "Sending..." : "Resend Verification Email"}
                </button>
              )}
              <button
                className="action-button"
                onClick={handleRetry}
                style={{
                  marginLeft: status.canResend ? "10px" : "0",
                }}
              >
                Try Again
              </button>
              <button
                className="action-button"
                onClick={handleLogin}
                style={{ marginLeft: "10px", backgroundColor: "#1565c0" }}
              >
                Login
              </button>
              <button
                className="action-button"
                onClick={handleGoHome}
                style={{ marginLeft: "10px", backgroundColor: "#555" }}
              >
                Go Home
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default VerifyEmail;
